"use client";

import { useState } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { WhatsNew } from "@/common/model/WhatsNew";
import { WhatsNewService } from "@/services/WhatsNewService";

import WhatsNewTabs from "./WhatsNewTabs";
import WhatsNewList from "./WhatsNewList";
import WhatsNewModal from "./WhatsNewModal";
import ConfirmDeleteModal from "../shared/ConfirmDeleteModal";

type WhatsNewManagerProps = {
  whatsNewItems: WhatsNew[];
  selectedTab: string;
  onSelectTab: (tab: string) => void;
  setWhatsNewItems: (items: WhatsNew[]) => void;
};

const WhatsNewManager: React.FC<WhatsNewManagerProps> = ({
  whatsNewItems,
  selectedTab,
  onSelectTab,
  setWhatsNewItems,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentWhatsNew, setCurrentWhatsNew] = useState<WhatsNew | null>(null);

  const handleAddWhatsNew = () => {
    setCurrentWhatsNew(null);
    setIsModalOpen(true);
  };

  const handleEditWhatsNew = (whatsNew: WhatsNew) => {
    setCurrentWhatsNew(whatsNew);
    setIsModalOpen(true);
  };

  const handleDeleteWhatsNew = (whatsNew: WhatsNew) => {
    setCurrentWhatsNew(whatsNew);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (currentWhatsNew) {
      await WhatsNewService.delete(currentWhatsNew.id.toString());
      fetchWhatsNewItems();
      setCurrentWhatsNew(null);
      setIsDeleteModalOpen(false);
    }
  };

  const fetchWhatsNewItems = async () => {
    setError(null);
    const updatedItems = await WhatsNewService.fetchAll();
    let filteredItems = updatedItems;
    if (selectedTab !== "all") {
      filteredItems = filteredItems.filter(
        (item) => item.status === selectedTab
      );
    }
    setWhatsNewItems(filteredItems);
  };

  const saveWhatsNew = async (whatsNew: Partial<WhatsNew>) => {
    setError(null);
    try {
      if (currentWhatsNew) {
        await WhatsNewService.update(currentWhatsNew.id.toString(), whatsNew);
      } else {
        await WhatsNewService.create(whatsNew);
      }
      fetchWhatsNewItems();
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : "Unknown error");
    }
    setIsModalOpen(false);
  };

  return (
    <div className="min-h-screen p-2 lg:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="w-full flex flex-col lg:flex-row gap-4 justify-between items-center mb-8">
          <h1 className="w-full text-xl lg:text-3xl font-bold text-balance">
            What's New Notifications
          </h1>
          <Button onClick={handleAddWhatsNew}>
            <PlusIcon className="mr-2 size-5" /> Add Whats New
          </Button>
        </div>
        {error && (
          <p className="text-red-500 mb-4 text-center text-xl">{error}</p>
        )}
        <WhatsNewTabs
          onSelect={(tab: string) => onSelectTab(tab)}
          selectedTab={selectedTab}
        />
        <WhatsNewList
          whatsNewItems={whatsNewItems}
          onEdit={handleEditWhatsNew}
          onDelete={handleDeleteWhatsNew}
        />
        <WhatsNewModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={saveWhatsNew}
          whatsNew={currentWhatsNew}
        />
        <ConfirmDeleteModal
          isOpen={isDeleteModalOpen}
          itemName={currentWhatsNew?.title || ""}
          itemLabel="notification"
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={confirmDelete}
        />
      </div>
    </div>
  );
};

export default WhatsNewManager;
